import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useQueryClient } from '@unifyapps/network/react-query';
import { useCallback } from 'react';
import { getIdWithPrefix } from '../../../../../utils/id';
import { REQUEST_ID_PREFIX } from '../../../../const';
import type { OnActionArgs, OnActionType } from '../../../../context/ActionsProvider/context';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import DependencyFlowHelper from '../../../../helper/DependencyFlowHelper';
import type {
  DependencyFlowInstanceType,
  QueryRequestResult,
} from '../../../../slices/DataSourceControllerSlice/types';
import {
  useGetGlobalAppStateStoreState,
  useGlobalAppStateStore,
} from '../../../../stores/app/GlobalAppStateStore';
import {
  useGetAppDataSourceRecordStoreState,
  useGetDataSourceRecordStoreStateByInstanceId,
} from '../../../../stores/DataSourceRecordStore';
import { useGetGlobalStateStoreStateByInstanceId } from '../../../../stores/GlobalStateStore';
import { useAppUnstagedDataSourceIds } from '../../../../stores/hooks/state';
import { SHOULD_NOT_PROPAGATE_UP, SHOULD_PROPAGATE_UP } from '../../const';

type ControlDataSourcePayload = {
  dataSourceId?: string;
  method?: string;
  queryKeyPrefix?: string;
};

type DataSourceStateActions = {
  addDependencyFlows: (flow: DependencyFlowInstanceType) => void;
  triggerManualDataSource: (
    key: string,
    options: {
      dataSourceId: string;
      queryKeyPrefix?: string;
      callback: () => void;
    },
  ) => void;
};

type DataSourceDependencies = {
  getDataSource: (dataSourceId: string, instanceId?: string) => QueryRequestResult | undefined;
  getDependencyFlow: (instanceId?: string) => DependencyFlowInstanceType | undefined;
  getDataSourceRecord: (dataSourceId: string, instanceId?: string) => DataSourceEntity | undefined;
  getDataSourceStateActions: (
    dataSourceId: string,
    instanceId?: string,
  ) => DataSourceStateActions | undefined;
  getAutomaticDataSourceIds: (instanceId?: string) => string[];
};

const useControlDataSourceBase = (deps: DataSourceDependencies) => {
  const queryClient = useQueryClient();

  const onControlDataSource: OnActionType = useEventCallback(
    ({ action, actionContext, instanceId }: OnActionArgs) => {
      const payload = action.payload as ControlDataSourcePayload;
      const { dataSourceId, method, queryKeyPrefix } = payload;
      // returning promise to wait for dataSource response
      // If there is no dataSourceId, return true to propagate the event to the parent
      if (!dataSourceId) return Promise.resolve(SHOULD_PROPAGATE_UP);

      const dataSourceStateActions = deps.getDataSourceStateActions(dataSourceId, instanceId);

      if (!dataSourceStateActions) {
        console.error('DataSource state actions not found for dataSourceId', dataSourceId);
        return Promise.resolve(SHOULD_PROPAGATE_UP);
      }

      switch (method) {
        case 'trigger': {
          const dataSource = deps.getDataSource(dataSourceId, instanceId);
          const dsDependencyFlow = deps.getDependencyFlow(instanceId);
          const dataSourceRecord = deps.getDataSourceRecord(dataSourceId, instanceId);

          // if a data source is not found, i.e., it might have been deleted, return a resolved promise
          // But for entity data source , dataSourceRecord is not created in store. It is created at
          // run time so skipping this check using skipDataSourceRecordCheck else control data source wont work
          if (!dataSourceRecord && !actionContext.skipDataSourceRecordCheck) {
            return Promise.resolve(SHOULD_PROPAGATE_UP);
          }

          // get the identity of the dataSource
          const automaticDataSourceIds = deps.getAutomaticDataSourceIds(instanceId);
          const isAutomaticDataSourceLegacy = automaticDataSourceIds.includes(dataSourceId);
          const isAutomaticDataSource = DataSourceHelper.isAutomaticDataSource(dataSourceRecord);
          const isManualDataSource = DataSourceHelper.isManualDataSource(dataSourceRecord);

          // in case of an automatic data source, we only invalidate the query because
          // we expect the automatic data source to have been called via dependency flow manager
          const triggerAutomaticDataSource = async () => {
            if (dataSource?.queryKey) {
              await queryClient.invalidateQueries({
                queryKey: dataSource.queryKey,
              });
              // data source is handled, we do not want to propagate the event to the parent
              return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
            }

            // this automatic data source is not present in the global state store then we put it
            // staging in the dependency flow manager
            const isDataSourceUnstaged = DependencyFlowHelper.isDependencyUnstaged(
              dsDependencyFlow ?? {},
              dataSourceId,
            );

            if (isDataSourceUnstaged) {
              dataSourceStateActions.addDependencyFlows(
                DependencyFlowHelper.createStagingDependencyFlow({
                  ids: [dataSourceId],
                }),
              );
              // data source is staged, we do not want to propagate the event to the parent
              return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
            }

            // now there is no entry of the data source record in the global state store and
            // the data source is not staged in the dependency flow manager, so I don't what edge case this is
            console.error(
              'Unexpected with triggering automatic data source',
              dataSourceId,
              dsDependencyFlow,
            );

            // data source is not staged, we do not want to propagate the event to the parent
            return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
          };

          const triggerManualDataSource = () => {
            return new Promise<boolean>((resolve) => {
              const key = getIdWithPrefix(REQUEST_ID_PREFIX);
              dataSourceStateActions.triggerManualDataSource(key, {
                dataSourceId,
                queryKeyPrefix,
                // data source is handled, we do not want to propagate the event to the parent
                callback: () => resolve(SHOULD_NOT_PROPAGATE_UP),
              });
            });
          };

          // decide which trigger to call based on the dataSource type
          // ORDER is very IMPORTANT, because we want to act on new type of data source first before handling legacy
          if (isAutomaticDataSource) {
            return triggerAutomaticDataSource();
          } else if (isManualDataSource) {
            return triggerManualDataSource();
          } else if (isAutomaticDataSourceLegacy && dataSource?.queryKey) {
            return triggerAutomaticDataSource();
          } else if (!isAutomaticDataSourceLegacy || queryKeyPrefix) {
            return triggerManualDataSource();
          }

          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
        }
        default:
          return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
      }
    },
  );

  return { onControlDataSource };
};

export const useControlPageDataSource = () => {
  const getGlobalStateStoreState = useGetGlobalStateStoreStateByInstanceId();
  const getDataSourceRecordStoreState = useGetDataSourceRecordStoreStateByInstanceId();

  const getDataSource = useCallback(
    (dataSourceId: string, instanceId?: string) => {
      return getGlobalStateStoreState(instanceId).dataSources[dataSourceId];
    },
    [getGlobalStateStoreState],
  );

  const getDependencyFlow = useCallback(
    (instanceId?: string) => {
      return getGlobalStateStoreState(instanceId).dataSourceDependencyFlow;
    },
    [getGlobalStateStoreState],
  );

  const getDataSourceRecord = useCallback(
    (dataSourceId: string, instanceId?: string) => {
      return getDataSourceRecordStoreState(instanceId).dataSources[dataSourceId];
    },
    [getDataSourceRecordStoreState],
  );

  const getDataSourceStateActions = useCallback(
    (_dataSourceId: string, instanceId?: string) => {
      return getGlobalStateStoreState(instanceId).actions.dataSource;
    },
    [getGlobalStateStoreState],
  );

  const getAutomaticDataSourceIds = useCallback(
    (instanceId?: string) => {
      const flow = getGlobalStateStoreState(instanceId).dataSourceDependencyFlow;
      return DependencyFlowHelper.getUnstagedDependencies({
        dependencyFlowInstance: flow ?? {},
      });
    },
    [getGlobalStateStoreState],
  );

  return useControlDataSourceBase({
    getDataSource,
    getDependencyFlow,
    getDataSourceRecord,
    getDataSourceStateActions,
    getAutomaticDataSourceIds,
  });
};

export const useControlGlobalPageDataSource = () => {
  const dataSourceStateActions = useGlobalAppStateStore().hooks.useActions().dataSource;
  const { getGlobalAppStateStoreState } = useGetGlobalAppStateStoreState();
  const appUnstagedDataSourceIds = useAppUnstagedDataSourceIds();
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();

  const getDataSource = useCallback(
    (dataSourceId: string, _instanceId?: string) => {
      return getGlobalAppStateStoreState()?.dataSources[dataSourceId];
    },
    [getGlobalAppStateStoreState],
  );

  const getDependencyFlow = useCallback(
    (_instanceId?: string) => {
      return getGlobalAppStateStoreState()?.dataSourceDependencyFlow;
    },
    [getGlobalAppStateStoreState],
  );

  const getDataSourceRecord = useCallback(
    (dataSourceId: string, _instanceId?: string) => {
      return getAppDataSourceRecordStoreState().dataSources[dataSourceId];
    },
    [getAppDataSourceRecordStoreState],
  );

  const getDataSourceStateActions = useCallback(
    (_dataSourceId: string, _instanceId?: string) => {
      return dataSourceStateActions;
    },
    [dataSourceStateActions],
  );

  const getAutomaticDataSourceIds = useCallback(
    (_instanceId?: string) => {
      return appUnstagedDataSourceIds;
    },
    [appUnstagedDataSourceIds],
  );

  return useControlDataSourceBase({
    getDataSource,
    getDependencyFlow,
    getDataSourceRecord,
    getDataSourceStateActions,
    getAutomaticDataSourceIds,
  });
};
